/**
 * ResetPasswordPage — handles Supabase recovery links.
 * When a user clicks the "reset password" link from their email, Supabase
 * auto-parses the recovery token from the URL hash and fires a PASSWORD_RECOVERY
 * event. This page lets them set a new password.
 */
import { Eye, EyeOff } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { GOLD } from "../dashboard/constants/theme";
import { BAM_LOGO_PNG } from "../dashboard/constants/branding";
import { supabase } from "../supabaseClient";

// An org invite lands here too (?invite=1, or type=invite in the hash): same
// mechanics, first-time wording, and it opens on My Organization.
const IS_INVITE = typeof window !== "undefined" && (
  new URLSearchParams(window.location.search).get("invite") === "1" ||
  (window.location.hash || "").includes("type=invite")
);

export default function ResetPasswordPage({ onDone }) {
  // Clear the app's recovery flag (if any) then run a navigation. Used by every
  // exit path so the user isn't held on the reset screen after they're done.
  const leave = (fn) => { try { onDone && onDone(); } catch {} fn(); };
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  // True when the recovery link is missing/expired/invalid, so we can show a
  // "request a new link" message instead of hanging on "Verifying…" forever.
  const [linkError, setLinkError] = useState(false);
  const readyRef = useRef(false);
  const markReady = () => { readyRef.current = true; setSessionReady(true); setLinkError(false); };

  useEffect(() => {
    // The recovery link Supabase emails redirects to `<origin>/#/reset` and then
    // appends `#access_token=…&refresh_token=…&type=recovery`. Result is a URL
    // with TWO `#` chars; the browser treats everything after the first as the
    // fragment, so Supabase's auto-detect (which expects the hash to start with
    // `access_token=…`) misses the tokens entirely. Parse them ourselves and
    // call setSession so PASSWORD_RECOVERY fires.
    (async () => {
      const hash = window.location.hash || "";
      const tokenStart = hash.indexOf("access_token=");
      if (tokenStart !== -1) {
        const params = new URLSearchParams(hash.slice(tokenStart));
        const access_token = params.get("access_token");
        const refresh_token = params.get("refresh_token");
        if (access_token && refresh_token) {
          try { await supabase.auth.setSession({ access_token, refresh_token }); }
          catch (e) { /* expired/invalid token — handled by the timeout below */ }
        }
      }
      let data = null;
      try { ({ data } = await supabase.auth.getSession()); } catch {}
      if (data?.session) markReady();
    })();
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) {
        markReady();
      }
    });
    // If no valid recovery session materializes shortly, the link is bad.
    const t = setTimeout(() => { if (!readyRef.current) setLinkError(true); }, 4000);
    return () => { clearTimeout(t); listener?.subscription?.unsubscribe(); };
  }, []);

  const handleSubmit = async () => {
    if (!password || password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true); setError("");
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) throw err;
      setSuccess(true);
      // Off the ?recovery=1 URL, or the reload lands back on this screen.
      const dest = IS_INVITE ? "/#/org" : "/#/platform";
      setTimeout(() => { leave(() => { window.location.replace(dest); }); }, 2000);
    } catch (e) {
      setError(e.message || "Couldn't update password. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "#080808",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      fontFamily: "'DM Sans',sans-serif" }}>
      <div style={{ textAlign: "center", padding: 40, width: "100%", maxWidth: 420 }}>
        <img src={BAM_LOGO_PNG} alt="BAM" style={{ width: 56, height: 70, objectFit: "contain",
          marginBottom: 20, filter: "drop-shadow(0 0 20px rgba(226,221,159,0.3))" }} />
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, color: "#F5F0E8",
          letterSpacing: 2, marginBottom: 8 }}>
          {success ? (IS_INVITE ? "YOU'RE IN" : "PASSWORD UPDATED") : (IS_INVITE ? "WELCOME TO BAM" : "SET NEW PASSWORD")}
        </div>
        <div style={{ fontSize: 14, color: "#9A9488", marginBottom: 32 }}>
          {success ? "Redirecting you to the platform..." :
           sessionReady ? (IS_INVITE ? "Choose a password for your account. You'll use it to sign in on the web and in the app." : "Choose a new password for your account.") :
           linkError ? (IS_INVITE ? "This invite link has expired or was already used." : "This reset link has expired or is invalid.") :
           (IS_INVITE ? "Opening your invite..." : "Verifying reset link...")}
        </div>

        {success ? null : sessionReady ? (
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 13, color: "#9A9488", marginBottom: 6 }}>New Password</div>
            <div style={{ position: "relative", marginBottom: 14 }}>
              <input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleSubmit(); }}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                style={{ width: "100%", padding: "14px 48px 14px 18px", borderRadius: 10, fontSize: 16,
                  fontFamily: "'DM Sans',sans-serif", background: "#0E0E0E", color: "#F5F0E8",
                  border: "1px solid rgba(255,255,255,0.10)", boxSizing: "border-box" }} />
              <button type="button" onClick={() => setShowPw(v => !v)} aria-label={showPw ? "Hide password" : "Show password"}
                style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#9A9488", cursor: "pointer", padding: 10, display: "flex" }}>
                {showPw ? <EyeOff size={17}/> : <Eye size={17}/>}
              </button>
            </div>
            <div style={{ fontSize: 13, color: "#9A9488", marginBottom: 6 }}>Confirm Password</div>
            <input type={showPw ? "text" : "password"} value={confirm} onChange={e => setConfirm(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleSubmit(); }}
              placeholder="Repeat your password"
              autoComplete="new-password"
              style={{ width: "100%", padding: "14px 18px", borderRadius: 10, fontSize: 16,
                fontFamily: "'DM Sans',sans-serif", background: "#0E0E0E", color: "#F5F0E8",
                border: "1px solid rgba(255,255,255,0.10)", marginBottom: 14, boxSizing: "border-box" }} />
            {error && <div style={{ fontSize: 13, color: "#E06060", marginBottom: 14, padding: "10px 14px",
              borderRadius: 8, background: "rgba(224,96,96,0.1)", border: "1px solid rgba(224,96,96,0.3)" }}>{error}</div>}
            <button onClick={handleSubmit} disabled={submitting}
              style={{ width: "100%", padding: "16px", borderRadius: 10, fontSize: 15, fontWeight: 800,
                letterSpacing: 1.5, background: submitting ? "rgba(255,255,255,0.08)" : GOLD,
                color: submitting ? "#666" : "#111", border: "none",
                fontFamily: "'Bebas Neue',sans-serif", cursor: submitting ? "not-allowed" : "pointer", marginTop: 8 }}>
              {submitting ? "SAVING..." : (IS_INVITE ? "SET PASSWORD" : "UPDATE PASSWORD")}
            </button>
          </div>
        ) : linkError ? (
          <div>
            <div style={{ fontSize: 13, color: "#9A9488", lineHeight: 1.6, marginBottom: 18 }}>
              {IS_INVITE
                ? "Invite links expire after a while and work once. Your account is already set up: on the sign-in screen, tap Forgot password and enter this email to get in."
                : "Reset links expire after a short window and can only be used once. Request a fresh one and we'll email it right over."}
            </div>
            <button onClick={() => leave(() => { window.location.hash = "#/platform"; window.location.reload(); })}
              style={{ width: "100%", padding: "16px", borderRadius: 10, fontSize: 15, fontWeight: 800,
                letterSpacing: 1.5, background: GOLD, color: "#111", border: "none",
                fontFamily: "'Bebas Neue',sans-serif", cursor: "pointer" }}>
              {IS_INVITE ? "GO TO SIGN IN" : "REQUEST A NEW LINK"}
            </button>
          </div>
        ) : (
          <div style={{ padding: "20px 0", color: "#666", fontSize: 13 }}>Verifying reset link…</div>
        )}

        <div onClick={() => leave(() => { window.location.hash = "#/platform"; })} className="btn"
          style={{ marginTop: 20, fontSize: 13, color: "#9A9488", cursor: "pointer" }}>
          Back to sign in
        </div>
      </div>
    </div>
  );
}
